/**
 * «Підключення до VPP»: «Подати заявку», after it «Заявку подано» with the
 * date and «модель готуємо» (owner, 26.10.2026: «Коли модель буде готова, ми
 * відкриємо вам доступ до неї») — the rest is outside the app. Owner,
 * 25.10.2026: «скрыть сколько клиент будет потенциально зарабатывать» — the
 * client applies, Phage sends the modelling of the point; only the
 * administrator sees the VPP potential over 30 days and per year, counted
 * with the outage reserve (the API gives it to them only).
 */
import type { VppPotentialResponse } from 'shared';

import { apiStatus, errorText } from '../../../components/errors.js';
import { addDays, fmtDate, fmtDateTime, fmtMoney, localYmd } from '../../../components/format.js';
import { useApplication, useApply } from '../../../hooks/use-site-results.js';
import { useLocale } from '../../../i18n/locale-context.js';

export function PotentialCard({
  siteId,
  potential,
  reservePct,
  isOwner,
  isAdmin,
  tz,
}: {
  siteId: string;
  potential: VppPotentialResponse | null | undefined;
  reservePct: number;
  isOwner: boolean;
  isAdmin: boolean;
  tz: string;
}) {
  const { t, tf, locale } = useLocale();
  const application = useApplication(siteId);
  const apply = useApply(siteId);
  const ok = potential?.status === 'ok';

  let sub: string | null = null;
  if (potential && ok) {
    const computed = localYmd(potential.computed_at, tz);
    sub = tf('site.pot.sub', {
      from: fmtDate(addDays(computed, -potential.window_days)),
      to: fmtDate(addDays(computed, -1)),
      days: potential.days_used,
      at: fmtDateTime(potential.computed_at, tz),
    });
  }
  let reason: string | null = null;
  if (!potential) reason = t('site.pot.pending');
  else if (potential.status === 'no_osr_rate') reason = t('site.pot.no_osr');
  else if (potential.status === 'no_data') {
    reason = t(
      potential.reason === 'load_above_inverter_rating' ? 'site.pot.no_data_load' : 'site.pot.no_data',
    );
  }

  return (
    <section className="card" aria-labelledby="pot-title">
      <div className="card-head">
        <h2 id="pot-title">{t('site.pot.title')}</h2>
        {isAdmin && sub ? <span className="sub">{sub}</span> : null}
      </div>
      <p className="explain" style={{ margin: '0 0 14px' }}>
        {t(isAdmin ? 'site.pot.intro' : 'site.pot.intro_client')}
      </p>
      {!isAdmin ? null : ok ? (
        <div className="pot">
          <div className="big">
            <span className="label">{t('site.pot.window')}</span>
            <span className="value num">
              {fmtMoney(locale, potential.increment_window ?? 0)}
              <small>₴</small>
            </span>
          </div>
          <div className="big">
            <span className="label">{t('site.pot.year')}</span>
            <span className="value num">
              {fmtMoney(locale, potential.increment_year ?? 0)}
              <small>₴</small>
            </span>
          </div>
        </div>
      ) : (
        <p className="note">{reason}</p>
      )}
      {isAdmin ? (
        <>
          <p className="explain">{tf('site.pot.explain', { reserve: reservePct })}</p>
          <details>
            <summary>{t('site.pot.how')}</summary>
            <ol className="how">
              <li>{t('site.pot.how_1')}</li>
              <li>{t('site.pot.how_2')}</li>
              <li>{t('site.pot.how_3')}</li>
              <li>{t('site.pot.how_4')}</li>
            </ol>
          </details>
        </>
      ) : null}
      {isOwner ? (
        <div className="actions">
          {application.data ? (
            <span className="status-chip">
              {tf('site.pot.applied', { at: fmtDateTime(application.data.created_at, tz) })}
            </span>
          ) : (
            <button
              type="button"
              className="btn primary"
              disabled={apply.isPending || application.isLoading}
              onClick={() => apply.mutate(undefined, { onError: () => void application.refetch() })}
            >
              {t('site.pot.apply')}
            </button>
          )}
          {apply.error && !application.data ? (
            <p className="err" role="alert" style={{ width: '100%' }}>
              {apiStatus(apply.error) === 409 ? t('site.pot.apply_err') : errorText(apply.error, tf)}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
